<script>
import BaseHypothesis from '@/components/AttendanceForm/general/summary/BaseHypothesis.vue'
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

import axialAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesAxialAsasMixin'
import perifericAsasMixin from '@/components/AttendanceForm/espondiloartrites/mixins/espondiloartritesPerifericAsasMixin'
import { SYMPTOM_OPTIONS } from '@/components/AttendanceForm/espondiloartrites/constants/symptom'
import { MEDICINE_CATEGORIES } from '@/constants/medicine'
import { COMORBIDITIES_OPTIONS } from '@/components/AttendanceForm/general/constants/personalHistory'
import { PSYCHIATRIC_ANSWER_VALUES } from '@/components/AttendanceForm/general/constants/psychiatricScreening'

export default {
  mixins: [axialAsasMixin, perifericAsasMixin],
  extends: BaseHypothesis,
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey', 'attendance']),
    investigationPhaseData() {
      return this.getPhaseDataByKey('investigacao_pessoal')
    },
    psychiatricPhaseData() {
      return this.getPhaseDataByKey('triagem_psiquiatrica')
    },
    selectedSymptoms() {
      if (Object.keys(this.symptomsPhaseDataSymptoms || {}).length === 0) {
        return null
      }

      return Object.entries(this.symptomsPhaseDataSymptoms || {})
        .map((e) => ({ key: e[0], data: e[1] }))
        .filter((s) => s.data.value)
        .map(
          (s) =>
            SYMPTOM_OPTIONS.find((o) => o.key === s.key)?.name?.firstLetterToLowercase() +
            (s.data?.period.length
              ? ` há ${s.data.period.length} ${
                  this.periodText[s.data.period.type][s.data.period.length > 1 ? 'plural' : 'singular']
                }`
              : ''),
        )
        .join(', ')
    },
    medicineHistory() {
      return Object.values(this.getPhaseDataByKey('historico_medicamentoso')?.medicines || {}) || []
    },
    currentMedicinesText() {
      let currentMedicines = this.medicineHistory.filter(
        (m) => m.periods.length !== 0 && m.periods.some((p) => !!p.start && !p.end),
      )

      if (currentMedicines.length === 0) {
        return 'nenhum'
      }

      return currentMedicines
        .map(
          (m) =>
            `${m.medicine.name.firstLetterToLowercase()}${
              m.periods.some((p) => !!p.start && !p.end)
                ? ' (desde ' + this.$util.toFormattedDate(m.periods.find((p) => !p.end).start) + ')'
                : ''
            }`,
        )
        .join(', ')
    },
    previousSistemicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.SISTEMICOS_TRADICIONAIS)
    },
    previousImunobiologicMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.IMUNOBIOLOGICOS)
    },
    previousAntiinflamatoryMedicinesText() {
      return this.getPreviousMedicineText(MEDICINE_CATEGORIES.ANTI_INFLAMATORIOS)
    },
    comorbiditiesText() {
      if (
        !this.investigationPhaseData ||
        this.investigationPhaseData.noDisease ||
        !this.investigationPhaseData.diseases ||
        this.investigationPhaseData.diseases.length === 0
      ) {
        return null
      }

      return this.investigationPhaseData.diseases
        .map(
          (d) =>
            COMORBIDITIES_OPTIONS.find((o) => o.key === d)?.name +
            (d === 'outras_comorbidades' ? ` (${this.investigationPhaseData.otherDisease})` : ''),
        )
        .join(', ')
    },
    previousTuberculosisText() {
      let previousTuberculosis = this.investigationPhaseData?.profile?.tuberculose?.value != 'NÃO' ?? null
      if (previousTuberculosis === null) {
        return 'Histórico de tuberculose prévia não respondido'
      } else if (previousTuberculosis) {
        return 'Tem histórico de tuberculose prévia'
      } else {
        return 'Não tem histórico de tuberculose prévia'
      }
    },
    tuberculosisRiskText() {
      let tuberculosisRisk = this.investigationPhaseData?.profile?.tuberculose_latente?.value ?? null
      if (tuberculosisRisk === null) {
        return 'Risco para contato com tuberculose não respondido'
      } else if (tuberculosisRisk) {
        return 'Tem alto risco para contato com tuberculose'
      } else {
        return 'Não tem alto risco para contato com tuberculose'
      }
    },
    anxietyScore() {
      const firstAnswer =
        this.psychiatricPhaseData?.profile['nervoso_ansioso_limite_duas_ultimas_semanas']?.value || 'NUNCA'
      const secondAnswer =
        this.psychiatricPhaseData?.profile['incapaz_controlar_preocupacoes_duas_ultimas_semanas']?.value || 'NUNCA'

      return PSYCHIATRIC_ANSWER_VALUES[firstAnswer] + PSYCHIATRIC_ANSWER_VALUES[secondAnswer]
    },
    depressionScore() {
      const firstAnswer =
        this.psychiatricPhaseData?.profile['pouco_interesse_prazer_duas_ultimas_semanas']?.value || 'NUNCA'
      const secondAnswer = this.psychiatricPhaseData?.profile['deprimido_duas_ultimas_semanas']?.value || 'NUNCA'

      return PSYCHIATRIC_ANSWER_VALUES[firstAnswer] + PSYCHIATRIC_ANSWER_VALUES[secondAnswer]
    },
    healthPlanText() {
      return this.attendance.healthPlan
        ? `Com acesso à rede privada ${this.attendance.healthPlan}`
        : 'Com acesso à rede pública SUS'
    },
    inflammatoryPain() {
      return this.painOptions.filter((o) => this.phaseData[o.key]).length >= 4
    },
    predominantSymptom() {
      return this.getPhaseDataByKey('sintomas')?.predominantSymptom
    },
  },
  data() {
    return {
      resultText: {
        '+': 'positivo',
        '-': 'negativo',
        '?': 'indefinido',
      },
      periodText: {
        MES: {
          singular: 'mês',
          plural: 'meses',
        },
        ANO: {
          singular: 'ano',
          plural: 'anos',
        },
      },
    }
  },
  methods: {
    getHypothesisText() {
      const age = this.$util.toAge(this.attendance.patient.birthday)
      const symptoms = this.selectedSymptoms ? `com os seguintes sintomas: ${this.selectedSymptoms}` : 'sem sintomas'
      const caspar = this.computedCaspar === '?' ? 'indefinido' : this.computedCaspar

      const sacroileiteImagemCriteria = `sacroileíte em imagem ${this.resultText[this.computedSacroileiteImagem]}`
      const symptomsCriteria =
        this.predominantSymptom === 'AXIAL'
          ? `ASAS Axial ${this.resultText[this.computedAsasAxial]}, ${sacroileiteImagemCriteria} e ${
              this.computedCaracteristicas
            } ${this.computedCaracteristicas === 1 ? 'critério' : 'critérios'} de espondiloartrites`
          : `ASAS Periférico ${this.resultText[this.computedAsasPeriferico]}, ${sacroileiteImagemCriteria} e ${
              this.computedArticulacoesDolorosas
            } ${this.computedArticulacoesDolorosas === 1 ? 'articulação dolorida' : 'articulações doloridas'}`

      const basdai = this.computedBasdai === '?' ? 'indefinido' : this.computedBasdai
      const asdasPcr = this.computedAsdasPcr === '?' ? 'indefinido' : this.computedAsdasPcr
      const asdasVhs = this.computedAsdasVhs === '?' ? 'indefinido' : this.computedAsdasVhs
      const eva = this.computedEva === '?' ? 'indefinido' : this.computedEva
      const das28 = this.computedDas28 === '?' ? 'indefinido' : this.computedDas28
      const dapsa = this.computedDapsa === '?' ? 'indefinido' : this.computedDapsa
      const diseaseActivityCriteria =
        this.predominantSymptom === 'AXIAL'
          ? `BASDAI ${basdai}, ASDAS PCR ${asdasPcr}, ASDAS VHS ${asdasVhs}, EVA ${eva}`
          : `DAS-28 ${das28}, DAPSA ${dapsa}, EVA ${eva}`

      const comorbidity =
        !this.investigationPhaseData ||
        this.investigationPhaseData?.noDisease ||
        this.investigationPhaseData?.diseases?.length === 0
          ? 'Não apresenta comorbidades'
          : `Apresenta as seguintes comorbidades: ${this.comorbiditiesText}`

      const anxiety = this.anxietyScore >= 2 ? 'Suspeita de transtorno de ansiedade' : null
      const depression = this.depressionScore >= 2 ? 'Suspeita de transtorno depressivo' : null

      const paragraphs = [
        `Paciente de ${age} anos ${symptoms}.`,
        `Apresenta critério ${symptomsCriteria}.`,
        `\nClassificação CASPAR: ${caspar}.`,
        `Atividade da doença conforme os critérios: ${diseaseActivityCriteria}.`,
        '\nHistórico de medicamentos:',
        `    - Medicamentos atuais: ${this.currentMedicinesText};`,
        `    - DMARDs e sistêmicos não biológicos prévios: ${this.previousSistemicMedicinesText};`,
        `    - Imunobiológicos ou pequenas moléculas prévios: ${this.previousImunobiologicMedicinesText};`,
        `    - Anti-inflamatórios prévios: ${this.previousAntiinflamatoryMedicinesText}.\n`,
        `${comorbidity}.`,
        `${this.previousTuberculosisText}.`,
        `${this.tuberculosisRiskText}.`,
      ]

      if (anxiety) {
        paragraphs.push(`${anxiety}.`)
      }
      if (depression) {
        paragraphs.push(`${depression}.`)
      }

      paragraphs.push(`\n${this.healthPlanText}.`)

      return paragraphs.join('\n')
    },
    getPreviousMedicineText(type) {
      let previousMedicines = this.medicineHistory.filter(
        (m) => (m.periods.length === 0 || m.periods.some((p) => !!p.end)) && m.medicine.category.id === type,
      )

      if (previousMedicines.length === 0) {
        return 'nenhum'
      }

      return previousMedicines
        .map((m) => {
          if (m.periods.length === 0) {
            return `${m.medicine.name}`
          }

          let periods = m.periods
            .filter((p) => !!p.end)
            .map((p) => `de ${this.$util.toFormattedDate(p.start)} a ${this.$util.toFormattedDate(p.end)}`)
            .join(', ')

          return `${m.medicine.name} (${periods})`
        })
        .join(', ')
    },
  },
}
</script>

<style scoped></style>
